import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { Loaded } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { call, CALLABLES } from "@/services/callables";
import { provisionDevice } from "./provisioning/flow";
import type { StepProgress, StepState } from "./provisioning/flow";
import { PROVISIONING_STEPS, ProvisioningError } from "./provisioning/link";
import type { DeviceLink, ProvisioningStep } from "./provisioning/link";
import { FIRMWARE_MANIFEST_URL, loadManifest } from "./provisioning/manifest";
import type { FirmwareManifest } from "./provisioning/manifest";
import { isWebSerialSupported, requestDeviceLink } from "./provisioning/webSerial";
import type { DeviceEnrolment, Kit } from "@/types";

/**
 * ## Devices → Add devices
 *
 * K2. Plug a device into this laptop, choose the kit it belongs to, press one
 * button: the current firmware is written, the kit's network is handed over,
 * the device says who it is, and it is registered — with its label made up
 * from the kit's name. The person never types a key or an id, and never sees
 * the network password.
 *
 * Every step is shown as it happens, and a failure says in plain words what
 * to do. It works in Chrome or Edge on a laptop, because those are the
 * browsers that can talk to a cable; elsewhere it says so and stops.
 */

type RequestLink = typeof requestDeviceLink;

export function AddDevicesPage({
  supported = isWebSerialSupported(),
  requestLink = requestDeviceLink,
}: {
  /** Injected by the unit tests, which have no browser and no cable. */
  supported?: boolean;
  requestLink?: RequestLink;
}) {
  const t = useT();
  // Not asked for on a browser that cannot use one.
  const kits = useCallable<{ kits: Kit[] }>(CALLABLES.listKits, undefined, supported);

  return (
    <SectionShell titleKey="devices.add.title">
      <p className="max-w-2xl text-ink-muted">{t("devices.add.intro")}</p>

      {supported ? (
        <div className="mt-8">
          <Loaded state={kits}>
            {(loaded) =>
              loaded.kits.length === 0 ? (
                <EmptyState
                  titleKey="devices.add.noKits"
                  bodyKey="devices.add.noKitsBody"
                  action={
                    <Link to="/kits" className="text-sm font-medium text-brand underline underline-offset-2 hover:text-brand-hover">
                      {t("nav.kits")}
                    </Link>
                  }
                />
              ) : (
                <Provisioner kits={loaded.kits} requestLink={requestLink} />
              )
            }
          </Loaded>
        </div>
      ) : (
        <div
          role="status"
          className="mt-8 max-w-2xl rounded-card border border-warning bg-warning-soft px-4 py-3 text-sm text-ink-soft"
        >
          {t("devices.add.browser")}
        </div>
      )}

      <p className="mt-10 text-sm text-ink-muted">
        <Link to="/devices" className="font-medium text-brand underline underline-offset-2 hover:text-brand-hover">
          {t("devices.add.back")}
        </Link>
      </p>
    </SectionShell>
  );
}

type Steps = Record<ProvisioningStep, { state: StepState; percent?: number }>;

const IDLE: Steps = {
  flash: { state: "pending" },
  network: { state: "pending" },
  identity: { state: "pending" },
  enrol: { state: "pending" },
};

function Provisioner({ kits, requestLink }: { kits: Kit[]; requestLink: RequestLink }) {
  const t = useT();
  const { showError } = useToast();
  const kitSelectId = useId();
  const [kitId, setKitId] = useState(kits[0]!.id);
  const [manifest, setManifest] = useState<FirmwareManifest | "failed" | undefined>(undefined);
  const [steps, setSteps] = useState<Steps>(IDLE);
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<ProvisioningError | undefined>(undefined);
  const [added, setAdded] = useState<DeviceEnrolment[]>([]);
  const [last, setLast] = useState<DeviceEnrolment | undefined>(undefined);
  /*
    Whether the firmware reached the device this session.

    The first real device took its software and then did not come back up, and
    the page had nothing to say but the error: the one button still read "Add
    the plugged-in device", and pressing it would have written 1.4 MB again to
    a device that already had it. Remembering this is what lets the page offer
    to carry on instead (2026-09-12).
  */
  const [flashed, setFlashed] = useState(false);
  /*
    The device is registered and its screen never came up.

    Registering worked, so this is not a failure of the flow — it is a device
    that will not be usable in a lesson, and the person holding a black
    rectangle has no other way to find that out (2026-09-12).
  */
  const [dark, setDark] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadManifest()
      .then((loaded) => {
        if (!cancelled) setManifest(loaded);
      })
      .catch(() => {
        if (!cancelled) setManifest("failed");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const kit = kits.find((candidate) => candidate.id === kitId) ?? kits[0]!;
  // A manifest that is only a placeholder is not one a device can be flashed from.
  const ready = manifest !== undefined && manifest !== "failed" && !manifest.note ? manifest : undefined;

  const report = (progress: StepProgress) => {
    if (progress.step === "flash" && progress.state === "done") setFlashed(true);
    setSteps((current) => ({
      ...current,
      [progress.step]: { state: progress.state, percent: progress.percent },
    }));
  };

  const start = async (options: { skipFlash?: boolean } = {}) => {
    if (!ready) return;
    setFailure(undefined);
    setLast(undefined);
    setDark(false);
    setSteps(options.skipFlash ? { ...IDLE, flash: { state: "done" } } : IDLE);
    if (!options.skipFlash) setFlashed(false);

    // The port picker first, inside the click: the browser shows it only on
    // a gesture, and a person who dismisses it has decided, not failed.
    let link: DeviceLink | undefined;
    try {
      link = await requestLink(ready, FIRMWARE_MANIFEST_URL);
    } catch (error) {
      if (error instanceof ProvisioningError) setFailure(error);
      else showError(error);
      return;
    }
    if (!link) return;

    setRunning(true);
    try {
      const { identity, enrolment } = await provisionDevice(
        link,
        { id: kit.id, ssid: kit.ssid, psk: kit.psk },
        async (input) => {
          const result = await call<typeof input, { enrolment: DeviceEnrolment }>(
            CALLABLES.enrolDevice,
            input
          );
          return result.enrolment;
        },
        report,
        { skipFlash: options.skipFlash === true }
      );
      setLast(enrolment);
      setDark(identity.board === "dark");
      setAdded((current) => [...current, enrolment]);
    } catch (error) {
      if (error instanceof ProvisioningError) {
        setFailure(error);
      } else {
        showError(error);
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {/*
        A plain select rather than `SelectField`: that one translates its
        options, and a kit's name is a name, not a key.
      */}
      <div>
        <label className="field-label" htmlFor={kitSelectId}>
          {t("devices.add.kit")}
        </label>
        <select
          id={kitSelectId}
          className="field-input"
          value={kit.id}
          disabled={running}
          onChange={(event) => setKitId(event.target.value)}
        >
          {kits.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
        <p className="mt-1.5 text-sm text-ink-muted">{t("devices.add.kitHint")}</p>
      </div>

      <p className="mt-4 text-sm text-ink-muted">
        {manifest === undefined
          ? t("common.loading")
          : manifest === "failed"
            ? t("devices.add.firmwareUnavailable")
            : t("devices.add.firmware", { version: manifest.version })}
      </p>
      {manifest !== undefined && manifest !== "failed" && manifest.note ? (
        <p role="status" className="mt-2 text-sm text-danger">
          {t("kits.firmware.placeholder")}
        </p>
      ) : null}

      {/*
        After a failure the way forward is named, and there are two of them.

        A device that took its software and then did not come back up needs
        unplugging and plugging in again — and then it needs the flow to carry
        on from where it stopped, not to write 1.4 MB it already has. The page
        used to leave the one button saying "Add the plugged-in device" beside
        a red step, which reads like a dead end and is why the first real
        device looked like one (2026-09-12).
      */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {failure && flashed && failure.step !== "flash" ? (
          <>
            <Button busy={running} disabled={!ready} onClick={() => void start({ skipFlash: true })}>
              {t("devices.add.resume")}
            </Button>
            <Button variant="secondary" disabled={running || !ready} onClick={() => void start()}>
              {t("devices.add.startOver")}
            </Button>
          </>
        ) : (
          <Button busy={running} disabled={!ready} onClick={() => void start()}>
            {t(failure ? "devices.add.retry" : "devices.add.start")}
          </Button>
        )}
      </div>

      {failure && flashed && failure.step !== "flash" ? (
        <p className="mt-2 max-w-2xl text-sm text-ink-muted">{t("devices.add.resumeHint")}</p>
      ) : null}

      <ol aria-label={t("devices.add.steps")} className="mt-8 flex list-decimal flex-col gap-3 pl-6">
        {PROVISIONING_STEPS.map((step) => {
          const current = steps[step];
          const failedHere = failure?.step === step;
          return (
            <li
              key={step}
              className={`rounded-card border px-4 py-3 text-sm ${
                current.state === "done"
                  ? "border-positive bg-white"
                  : current.state === "failed" || failedHere
                    ? "border-danger bg-white"
                    : current.state === "running"
                      ? "border-warning bg-warning-soft"
                      : "border-line bg-white text-ink-muted"
              }`}
            >
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-ink">{t(`devices.add.step.${step}`)}</span>
                <span className="text-ink-muted">
                  {failedHere
                    ? t("devices.add.state.failed")
                    : current.state === "running" && step === "flash" && current.percent !== undefined
                      ? t("devices.add.state.writing", { percent: current.percent })
                      : t(`devices.add.state.${current.state}`)}
                </span>
              </div>
              {failedHere ? (
                <p role="alert" className="mt-2 text-danger">
                  {t(failure.key, failure.params)}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      {last ? (
        <div
          role="status"
          className="mt-6 rounded-card border border-positive bg-white px-4 py-3 text-sm"
        >
          <p className="text-ink">{t("devices.add.done", { label: last.deviceLabel })}</p>
          <p className="mt-1 font-mono text-xs text-ink-muted">{last.deviceId}</p>
        </div>
      ) : null}

      {dark ? (
        <div
          role="alert"
          className="mt-4 rounded-card border border-warning bg-warning-soft px-4 py-3 text-sm text-ink-soft"
        >
          {t("devices.add.screenDark")}
        </div>
      ) : null}

      {added.length > 0 ? (
        <p className="mt-4 text-sm text-ink-muted">
          {t("devices.add.count", { count: added.length, kit: kit.name })}
        </p>
      ) : null}
    </div>
  );
}
